import PrimaryButton from '@/components/Buttons/PrimaryButton';
import EmptyState from '@/components/common/EmptyState';
import StatusBadge from '@/components/common/StatusBadge';
import TableHeader from '@/components/common/TableHeader';
import ThemeWrapper from '@/components/ThemeWrapper';
import usePermissions from '@/hooks/usePermissions';
import useRoutes from '@/hooks/useRoutes';
import StaffLayout from '@/layouts/StaffLayout';
import { User } from '@/types';
import { Link } from '@inertiajs/react';
import { Edit, UserPlus } from 'lucide-react';
import React from 'react';

interface CommercialUsersIndexProps {
    users: User[];
}

const IndexContent: React.FC<CommercialUsersIndexProps> = ({ users }) => {
    const { canManageCommercialUsers } = usePermissions();
    const { route } = useRoutes();

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            Usuarios Comerciales
                        </h1>
                        <p className="mt-2 text-gray-600 dark:text-neutral-300">
                            Gestión de usuarios del equipo comercial
                        </p>
                    </div>
                    {canManageCommercialUsers && (
                        <Link
                            href={route(
                                'staff-commercial-users-create'
                            ).toString()}
                        >
                            <PrimaryButton>
                                <UserPlus size={16} className="mr-2" />
                                Crear Usuario
                            </PrimaryButton>
                        </Link>
                    )}
                </div>

                {users.length === 0 ? (
                    <EmptyState
                        icon={UserPlus}
                        title="No hay usuarios comerciales"
                        description="Comienza creando tu primer usuario comercial."
                        action={
                            canManageCommercialUsers ? (
                                <Link
                                    href={route(
                                        'staff-commercial-users-create'
                                    ).toString()}
                                >
                                    <PrimaryButton>
                                        <UserPlus size={16} className="mr-2" />
                                        Crear Usuario
                                    </PrimaryButton>
                                </Link>
                            ) : undefined
                        }
                    />
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                            <TableHeader
                                columns={[
                                    { label: 'Nombre' },
                                    { label: 'Email' },
                                    { label: 'Academia' },
                                    { label: 'Estado' },
                                    ...(canManageCommercialUsers
                                        ? [
                                              {
                                                  label: 'Acciones',
                                                  align: 'right' as const,
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-neutral-700 dark:bg-neutral-800">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {user.full_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {user.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {user.current_academy?.name ||
                                                    '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge
                                                status={
                                                    user.is_active
                                                        ? 'active'
                                                        : 'inactive'
                                                }
                                            />
                                        </td>
                                        {canManageCommercialUsers && (
                                            <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                                                <Link
                                                    href={route(
                                                        'staff-commercial-users-edit',
                                                        { user_id: user.id }
                                                    ).toString()}
                                                    className="mr-3 inline-flex items-center text-blue-600 hover:text-blue-900"
                                                >
                                                    <Edit size={16} />
                                                </Link>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<CommercialUsersIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
